/**
 * Data plane — Dataset Semantics (L2) API client.
 *
 * Mirrors the semantics endpoints on `backend/src/hub/routes/datasets.py`
 * (ADR-0026). The meaning of a dataset, field by field. `get` returns the merged
 * view (every live field + its annotation + orphans); `save` PUTs the descriptor;
 * `propose` asks Odino to draft one (not saved); `vocab` is the suggestion lists.
 */
import { api } from "./api";

export type Sensitivity = "public" | "internal" | "confidential" | "restricted";

/** Per-field meaning. Free-string `semantic_type`/`role`; null `sensitivity` inherits. */
export interface FieldSemantic {
  label?: string | null;
  description?: string | null;
  semantic_type?: string | null;
  unit?: string | null;
  sensitivity?: Sensitivity | null;
  synonyms?: string[];
  role?: string | null;
}

/** A row in the merged view: a live field + its (optional) annotation. */
export interface SemanticFieldRow extends FieldSemantic {
  name: string;
  /** Raw DB/column type (present for live fields; absent for orphans). */
  type?: string | null;
}

/** Schema-cache status for the editor badge — external datasets only. `cached`
 * means this load's fields came from the cached introspection (the remote read is
 * skipped); `cached_at` is when they were fetched; `ttl_seconds` the freshness window. */
export interface SchemaCacheStatus {
  cacheable: boolean;
  cached: boolean;
  cached_at: string | null;
  ttl_seconds: number;
}

/** GET response — dataset-level meaning + merged field rows + orphaned annotations. */
export interface DatasetSemanticsView {
  dataset_id: string;
  label: string | null;
  description: string | null;
  domain: string | null;
  sensitivity: Sensitivity;
  fields: SemanticFieldRow[];
  orphans: SemanticFieldRow[];
  /** Present for external datasets (the schema is introspected + cached). */
  schema_cache?: SchemaCacheStatus;
}

/** PUT body / propose result — the stored shape (fields keyed by name). */
export interface DatasetSemanticsInput {
  label?: string | null;
  description?: string | null;
  domain?: string | null;
  sensitivity: Sensitivity;
  fields: Record<string, FieldSemantic>;
}

export interface SemanticsVocab {
  sensitivity: string[];
  semantic_types: string[];
  roles: string[];
  /** ADR-0029 declared-metric aggregation functions (the validated vocabulary). */
  aggregations: string[];
}

/** One dataset's L2 status for the instance-wide Semantica catalog. */
export interface SemanticsOverviewRow {
  dataset_id: string;
  name: string;
  owner: string;
  binding: "internal" | "external";
  published: boolean;
  has_semantics: boolean;
  annotated_count: number;
  /** Live field count — null when an external source can't be reached. */
  field_count: number | null;
  sensitivity: string | null;
  domain: string | null;
  has_pii: boolean;
  updated_at: string | null;
}

export const datasetSemanticsApi = {
  vocab: () => api.get<SemanticsVocab>("/datasets/semantics/vocab"),

  /** Instance-wide L2 coverage table (Semantica catalog page). */
  overview: () => api.get<SemanticsOverviewRow[]>("/datasets/semantics/overview"),

  get: (datasetId: string) =>
    api.get<DatasetSemanticsView>(`/datasets/${datasetId}/semantics`),

  save: (datasetId: string, body: DatasetSemanticsInput) =>
    api.put<DatasetSemanticsView>(`/datasets/${datasetId}/semantics`, body),

  /** AI bootstrap — returns a draft for review (nothing saved). */
  propose: (datasetId: string) =>
    api.post<DatasetSemanticsInput>(`/datasets/${datasetId}/semantics/propose`, {}),

  /** Manual "Ricarica schema": drop the cached field list + the source's warm
   * engine, re-read the live schema, return the fresh view (+ warm schema_cache). */
  refreshSchema: (datasetId: string) =>
    api.post<DatasetSemanticsView>(`/datasets/${datasetId}/semantics/refresh-schema`, {}),
};
